import React, { FC, useMemo } from 'react';
import { useHistory } from 'react-router';

import { EVINCED_SEVERITIES, EvSection, EvSpinner } from '@evinced-private/ui-common';

import {
	MOBILE_SDK_TEST_BREAKDOWN_BY_SEVERITY_PIE_CHART_DAID,
	MOBILE_SDK_TEST_OVERVIEW_SUMMARY_DAID,
	MOBILE_SDK_TEST_OVERVIEW_TAB_DAID,
	MOBILE_SDK_TEST_SCAN_SEVERITIES_GRAPH_DAID,
	MOBILE_SDK_TEST_TOP_ISSUE_TYPES_GRAPH_DAID,
	MOBILE_SDK_TOTAL_ISSUES_BREAKDOWN_BY_ISSUE_TYPE_CHART_DAID
} from 'data-automation-ids/MobileSdkDashboardAutomationIds';
import { createMobileSummaryItems } from 'src/apps/mobile-common/helpers/MobileSummaryPaneHelper';
import { useMobileSdkTestIssueTypesQuery } from 'src/apps/mobile-sdk/hooks/useMobileSdkTestIssueTypesQuery';
import { useMobileSdkTestSeveritiesQuery } from 'src/apps/mobile-sdk/hooks/useMobileSdkTestSeveritiesQuery';
import { useMobileSdkTestSummaryQuery } from 'src/apps/mobile-sdk/hooks/useMobileSdkTestSummaryQuery';
import { TMobileSdkTestFilterOptions } from 'src/apps/mobile-sdk/types/MobileSdkServiceTypes';
import { MobileBreakdownBySeverityPieChart } from 'src/common/components/mobile-breakdown-by-severity-chart/MobileBreakdownBySeverityPieChart';
import { MobileScansImpactedBySeverityChart } from 'src/common/components/mobile-scans-impacted-by-severity-chart/MobileScansImpactedBySeverityChart';
import { MobileSummaryPane } from 'src/common/components/mobile-summary-pane/MobileSummaryPane';
import { MobileTopIssueTypesBarChart } from 'src/common/components/mobile-top-issue-types-bar-chart/MobileTopIssueTypesBarChart';
import { MobileTotalIssuesBreakdownByIssueTypeChart } from 'src/common/components/mobile-total-issues-breakdown-by-issue-type-chart/MobileTotalIssuesBreakdownByIssueTypeChart';
import { IFilterOptions } from 'src/common/helpers/DataFilterHelper';
import { MOBILE_SEVERITY_MAP } from 'src/common/helpers/MobileQueriesHelper';
import { RoutesHelper } from 'src/common/helpers/RoutesHelper';
import { buildFullTableId } from 'src/common/helpers/TableStateHelper';
import { ProductType } from 'src/common/interfaces/Tenant';
import { MOBILE_SDK_TEST_ALL_ISSUES_TABLE_ID } from 'src/common/pages/dashboards-container/dashboard_consts';
import { useTableState } from 'src/common/providers/dashboard/TableStateProvider';
import { SessionViews } from 'src/common/types/WfaSessionViews';

import './MobileSdkSessionOverview.scss';

interface IMobileSdkSessionOverviewProps {
	testId: string;
}

export const MobileSdkSessionOverview: FC<IMobileSdkSessionOverviewProps> = ({ testId }) => {
	const history = useHistory();
	const { onFilterChange } = useTableState();

	const testSummaryQuery = useMobileSdkTestSummaryQuery({
		testId
	});
	const issueTypesQuery = useMobileSdkTestIssueTypesQuery({
		testId
	});
	const severitiesQuery = useMobileSdkTestSeveritiesQuery({
		testId
	});

	const summaryItems = useMemo(() => {
		if (!testSummaryQuery.data) {
			return [];
		}

		return createMobileSummaryItems(testSummaryQuery.data, ProductType.MOBILE_SDK);
	}, [testSummaryQuery.data]);

	const issuesTableIdForTest = useMemo(
		() => buildFullTableId(MOBILE_SDK_TEST_ALL_ISSUES_TABLE_ID, testId),
		[testId]
	);

	const onChartClick = (filters: IFilterOptions): void => {
		onFilterChange(issuesTableIdForTest, filters);
		history.push(RoutesHelper.getMobileSdkTestPath(testId, SessionViews.ALL_ISSUES), {
			from: history.location.pathname
		});
	};

	const handleSeverityChartClick = (severity: EVINCED_SEVERITIES): void => {
		onChartClick({ severities: [{ value: severity, label: MOBILE_SEVERITY_MAP[severity] }] });
	};

	const handleTopIssueTypesBarChartClick = (issueType: string): void => {
		onChartClick({ types: [{ value: issueType, label: issueType }] });
	};

	const handleBreakdownByIssueTypeBarClick = (issueType: string, scanName: string): void => {
		const filterOptions: TMobileSdkTestFilterOptions = {
			types: [{ value: issueType, label: issueType }],
			scans: [{ value: scanName, label: scanName }]
		};
		onChartClick(filterOptions);
	};

	return (
		<div className="mobile-sdk-session-overview">
			<EvSection
				className="mobile-summary-pane"
				dataAutomationId={MOBILE_SDK_TEST_OVERVIEW_TAB_DAID}
			>
				{!testSummaryQuery.data && testSummaryQuery.isLoading ? (
					<EvSpinner />
				) : (
					<MobileSummaryPane
						sectionLabel="Test summary"
						dataAutomationId={MOBILE_SDK_TEST_OVERVIEW_SUMMARY_DAID}
						items={summaryItems}
					/>
				)}
			</EvSection>
			<EvSection
				dataAutomationId={MOBILE_SDK_TOTAL_ISSUES_BREAKDOWN_BY_ISSUE_TYPE_CHART_DAID}
				skipLinkId="issue-types-chart-id"
			>
				{!issueTypesQuery.data && issueTypesQuery.isLoading ? (
					<EvSpinner />
				) : (
					<MobileTotalIssuesBreakdownByIssueTypeChart
						data={issueTypesQuery.data}
						onBarClick={handleBreakdownByIssueTypeBarClick}
					/>
				)}
			</EvSection>
			<div className="charts-container">
				<div className="charts-row">
					<EvSection
						className="pie-chart-section"
						dataAutomationId={MOBILE_SDK_TEST_BREAKDOWN_BY_SEVERITY_PIE_CHART_DAID}
						skipLinkId="breakdown-pie-chart-id"
					>
						{!severitiesQuery.data && severitiesQuery.isLoading ? (
							<EvSpinner />
						) : (
							<MobileBreakdownBySeverityPieChart
								severitiesCount={severitiesQuery.data}
								onPieClick={handleSeverityChartClick}
							/>
						)}
					</EvSection>
					<EvSection
						dataAutomationId={MOBILE_SDK_TEST_TOP_ISSUE_TYPES_GRAPH_DAID}
						skipLinkId="top-issue-types-chart-id"
					>
						{!issueTypesQuery.data && issueTypesQuery.isLoading ? (
							<EvSpinner />
						) : (
							<MobileTopIssueTypesBarChart
								data={issueTypesQuery.data}
								onBarClick={handleTopIssueTypesBarChartClick}
							/>
						)}
					</EvSection>
				</div>
				<div className="charts-row">
					<EvSection
						dataAutomationId={MOBILE_SDK_TEST_SCAN_SEVERITIES_GRAPH_DAID}
						skipLinkId="severities-chart-id"
					>
						{!severitiesQuery.data && severitiesQuery.isLoading ? (
							<EvSpinner />
						) : (
							<MobileScansImpactedBySeverityChart
								severitiesCount={severitiesQuery.data}
								onBarClick={handleSeverityChartClick}
							/>
						)}
					</EvSection>
				</div>
			</div>
		</div>
	);
};
